import React, { useState } from "react";

import { RcFile } from "antd/es/upload";
import moment from "moment";

import { parseProgramContract, updateProgramContractDescription } from "services";

import { getProgramInternalName, handleS3Download, persistentQueryClient, useGetCarbonProgramContractsForPrograms } from "utils";

import { GET_PROGRAM_CONTRACTS_ADMIN } from "consts";

import { CarbonProgram, ProgramContract } from "models";

import { Button, Flex, Form, Icon, ModalContent, Tag, TextArea, Tooltip, Typography, Upload, Message as message } from "components";

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const handleUpload = async (file: RcFile, programId: string, description: string | null = null) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    message({ content: `File exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB} MB.`, icon: "error" });
    return false;
  }

  await parseProgramContract({
    file,
    programId,
    description,
  });

  return false;
};

const handlePublishDescription = async (activeContract: ProgramContract, description: string, setIsPublishing: (isPublishing: boolean) => void) => {
  if (!activeContract) return;
  setIsPublishing(true);
  try {
    await updateProgramContractDescription({
      programId: activeContract.programId,
      contractId: activeContract.id,
      description: description.trim() || null,
    });
    persistentQueryClient.invalidateQueries({ queryKey: [GET_PROGRAM_CONTRACTS_ADMIN, activeContract.programId] });
  } finally {
    setIsPublishing(false);
  }
};

const ContractRow = ({ contract }: { contract: ProgramContract }) => {
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  return (
    <>
      <Flex align="center" justify="space-between">
        <div>
          {contract.replacedById === null && (
            <>
              <Tag colour="green" text="Active" />
              <br />
            </>
          )}
          <span className={contract.replacedById ? "grey-text" : ""}>ID: {contract.id}</span>
          <br />
          <span className={contract.replacedById ? "grey-text" : ""}>Uploaded at: {moment(contract.createdAt).format("DD.MM.YYYY")}</span>
        </div>
        <Flex align="center">
          <Tooltip title="View description">
            <Icon iconName="tabler/list" onClick={() => setDescriptionModalOpen(true)} className="pointer" />
          </Tooltip>
          <Tooltip title="Download contract">
            <Icon className="margin-left-100 pointer" iconName="tabler/download" onClick={() => handleS3Download(contract.link)} />
          </Tooltip>
        </Flex>
      </Flex>
      <ModalContent visible={descriptionModalOpen} onCancel={() => setDescriptionModalOpen(false)}>
        <Typography variant="h3" className="margin-bottom-200">
          Description
        </Typography>
        <Typography variant="paragraph-m" style={{ whiteSpace: "pre-line" }}>
          {contract.description || "No description set."}
        </Typography>
      </ModalContent>
      <hr className="margin-bottom-100" />
    </>
  );
};

const DescriptionForm = ({ activeContract, programName }: { activeContract: ProgramContract; programName: string }) => {
  const [description, setDescription] = useState<string>(activeContract?.description || "");
  const [isPublishing, setIsPublishing] = useState(false);

  return (
    <Form>
      <Form.Item className="margin-bottom-200" name="description">
        <Flex vertical align="center" gap="0.25rem">
          <TextArea
            name="description"
            initialValue={description}
            onChange={(e) => setDescription(e.target.value)}
            className="margin-bottom-100 active width-100"
            label="Notification shown to enrolled farmers, summarising the changes of the latest contract."
            autoSize={{ minRows: 4 }}
          />
          <Button
            type="primary"
            disabled={description.trim() === activeContract?.description?.trim() || isPublishing}
            loading={isPublishing}
            onClick={() => handlePublishDescription(activeContract, description, setIsPublishing)}
          >{`Publish description for active ${programName} contract`}</Button>
        </Flex>
      </Form.Item>
    </Form>
  );
};

export const ContractsContent = ({ carbonProgram }: { carbonProgram: CarbonProgram }) => {
  const carbonProgramContractQuery = useGetCarbonProgramContractsForPrograms([carbonProgram.id]);
  const existingContracts = carbonProgramContractQuery?.[0]?.data;
  const activeContract = existingContracts?.find((contract) => !contract.replacedById);
  const programName = getProgramInternalName(carbonProgram);

  return (
    <>
      {activeContract && <DescriptionForm activeContract={activeContract} programName={programName} />}
      <div className="margin-bottom-200">
        {existingContracts?.map((contract: ProgramContract) => <ContractRow contract={contract} key={contract.id} />) ?? null}
      </div>
      <Typography variant="paragraph-m">
        {existingContracts?.length
          ? "Replacing the existing contract for this program will mean all farmers who are enrolled in it, will see a popup where they are shown the new contract."
          : "No contract uploaded"}
      </Typography>
      <Upload name="file" showUploadList={false} action="" beforeUpload={(v) => handleUpload(v, carbonProgram.id)} className="auto-height contract-upload">
        <Button block>{existingContracts?.length ? "Replace contract" : "Upload contract"}</Button>
      </Upload>
    </>
  );
};
